import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { AuthConfigService } from "../modules/auth/_services/auth-config.service";
import { SocialOAuthProvider } from "../modules/auth/_models/social-oauth-provider.enum";
import { Response } from "../shared/models/Response";

export interface MiCuentaResumen {
   usuarioId: string;
   email: string;
   nombre: string;
   apellidos: string;
   emailVerificado: boolean;
   emailVerificadoEn?: string;
   tienePasswordConfigurada: boolean;
   avatarSeleccionado?: MiCuentaAvatar;
   avatars: MiCuentaAvatar[];
   metodosAcceso: MiCuentaMetodoAcceso[];
}

export interface MiCuentaMetodoAcceso {
   usuarioIdentidadAccesoId: string;
   tipoProveedorAccesoId: number;
   proveedorCodigo: string;
   proveedorNombre: string;
   proveedorUsuarioId: string;
   emailProveedor?: string;
   emailProveedorVerificado: boolean;
   nombreProveedor?: string;
   fechaEnlace: string;
   ultimoAcceso?: string;
}

export interface MiCuentaAvatar {
   usuarioAvatarId: string;
   usuarioIdentidadAccesoId?: string;
   avatarUrl?: string;
   archivoBase64?: string;
   mimeType?: string;
   esProveedor: boolean;
   seleccionado: boolean;
}

export interface SucursalStats {
   sucursalId: string;
   emisorId: string;
   totalUsuarios: number;
   totalDtesMesActual: number;
}

@Injectable({
   providedIn: "root",
})
export class MiCuentaService {
   private readonly endpoint: string;

   /** Emite el ultimo resumen cargado. Componentes globales (header) pueden escuchar para actualizar avatar. */
   private readonly _resumenSubject = new BehaviorSubject<MiCuentaResumen | null>(null);
   public readonly resumen$ = this._resumenSubject.asObservable();

   /** Construye una src de imagen a partir de un avatar (URL externa o data: base64). */
   public static avatarToSrc(avatar?: MiCuentaAvatar | null): string | null {
      if (!avatar) {
         return null;
      }
      if (avatar.avatarUrl) {
         return avatar.avatarUrl;
      }
      if (avatar.archivoBase64 && avatar.mimeType) {
         return `data:${avatar.mimeType};base64,${avatar.archivoBase64}`;
      }
      return null;
   }

   constructor(private http: HttpClient, private config: AuthConfigService) {
      this.endpoint = `${this.config.wepApiRoute}/MiCuenta`;
   }

   /**
    * Obtiene el resumen y lo publica en el subject compartido.
    * Cualquier componente que necesite el avatar/datos puede llamar a este metodo o suscribirse a `resumen$`.
    */
   getResumen(): Observable<Response<MiCuentaResumen>> {
      return this.http.get<Response<MiCuentaResumen>>(this.endpoint).pipe(
         tap((response) => {
            if (response && response.data) {
               this._resumenSubject.next(response.data);
            }
         })
      );
   }

   /** Limpia el cache (por ej. al cerrar sesion). */
   clearResumenCache(): void {
      this._resumenSubject.next(null);
   }

   getSucursalesStats(): Observable<Response<SucursalStats[]>> {
      return this.http.get<Response<SucursalStats[]>>(`${this.endpoint}/sucursales-stats`);
   }

   linkAccessMethod(code: string, provider: SocialOAuthProvider): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/metodos-acceso/link`, { code, provider });
   }

   unlinkAccessMethod(usuarioIdentidadAccesoId: string): Observable<Response<boolean>> {
      return this.http.delete<Response<boolean>>(`${this.endpoint}/metodos-acceso/${usuarioIdentidadAccesoId}`);
   }

   uploadAvatar(file: File): Observable<Response<string>> {
      const formData = new FormData();
      formData.append("file", file);
      return this.http.post<Response<string>>(`${this.endpoint}/avatar/upload`, formData);
   }

   selectAvatar(usuarioAvatarId: string): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/avatar/select/${usuarioAvatarId}`, null);
   }

   deleteAvatar(usuarioAvatarId: string): Observable<Response<boolean>> {
      return this.http.delete<Response<boolean>>(`${this.endpoint}/avatar/${usuarioAvatarId}`);
   }

   sendEmailVerificationCode(): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/email-verificacion/enviar`, null);
   }

   obtenerVigenciaCodigo(): Observable<Response<number>> {
      return this.http.get<Response<number>>(`${this.endpoint}/email-verificacion/vigencia`);
   }

   confirmEmailVerificationCode(codigo: string): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/email-verificacion/confirmar`, { codigo });
   }
}
